import { Link } from "react-router-dom";
import { Shield, Truck, Star, Eye, MapPin, ArrowRight, Sparkles, CheckCircle, Clock, Users, Award, Zap, ThumbsUp, Car } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { FadeUp, StaggerContainer, StaggerItem, SlideInLeft, SlideInRight, BlurIn, TextReveal, ScaleStaggerItem } from "@/components/AnimatedSection";
import PageTransition from "@/components/PageTransition";
import AnimatedCounter from "@/components/AnimatedCounter";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import ProcessTimeline from "@/components/ProcessTimeline";
import FAQSection from "@/components/FAQSection";
import InstagramFeed from "@/components/InstagramFeed";
import ParallaxSection from "@/components/ParallaxSection";
import TestimonialCarousel from "@/components/TestimonialCarousel";
import ReviewsHighlight from "@/components/ReviewsHighlight";
import ServiceAreaMap from "@/components/ServiceAreaMap";
import SocialProofBar from "@/components/SocialProofBar";
import CTABanner from "@/components/CTABanner";
import heroVideo from "@/assets/hero-video.mp4.asset.json";
import foundersDetailing from "@/assets/founders-detailing.jpg";
import foundersDetailingResponsive from "@/assets/founders-detailing.jpg?responsive";
import detailInspection from "@/assets/detail-inspection.jpg";
import interiorDetail from "@/assets/interior-detail.jpg";
import interiorDetailResponsive from "@/assets/interior-detail.jpg?responsive";
import exteriorResult from "@/assets/exterior-result.jpg";
import exteriorResultResponsive from "@/assets/exterior-result.jpg?responsive";
import wheelDetail from "@/assets/wheel-detail.jpg";
import beforeDetail from "@/assets/before-detail.jpg?responsive";
import afterDetail from "@/assets/after-detail.jpg?responsive";
import ResponsiveImage from "@/components/ResponsiveImage";

const services = [
  {
    title: "Full Detail",
    badge: "Recommended",
    description: "Our flagship service. Complete interior and exterior transformation in one visit — Essential, Signature, and Elite tiers starting at $198.99.",
    image: foundersDetailing,
    imageResponsive: foundersDetailingResponsive,
    tiers: "3 tiers from $198.99",
    icon: Sparkles,
    featured: true,
  },
  {
    title: "Interior Detail",
    badge: "Focused Service",
    description: "Interior-only maintenance for already-clean exteriors. Starting at $148.99.",
    image: interiorDetail,
    imageResponsive: interiorDetailResponsive,
    tiers: "3 tiers from $148.99",
    icon: Eye,
    featured: false,
  },
  {
    title: "Exterior Detail",
    badge: "Focused Service",
    description: "Exterior-only refresh for spotless interiors. Starting at $118.99.",
    image: exteriorResult,
    imageResponsive: exteriorResultResponsive,
    tiers: "3 tiers from $118.99",
    icon: Shield,
    featured: false,
  },
];

const whyUs = [
  { text: "We care more than other detailers", icon: "❤️", detail: "Every car is treated like our own" },
  { text: "We never rush a job", icon: "⏱️", detail: "Quality takes time — always" },
  { text: "We focus on details others miss", icon: "🔍", detail: "Door jambs, vents, every crevice" },
  { text: "We deliver real results", icon: "✨", detail: "See our before & after gallery" },
  { text: "We come to your location", icon: "🚗", detail: "Mobile to your home or office" },
];

const trustBadges = [
  { icon: Shield, label: "Fully Insured" },
  { icon: CheckCircle, label: "5-Star Rated" },
  { icon: Clock, label: "On-Time Guarantee" },
  { icon: Users, label: "500+ Happy Clients" },
];

const quickStats = [
  { icon: Car, value: "500+", label: "Cars Detailed" },
  { icon: Star, value: "5.0", label: "Star Rating" },
  { icon: ThumbsUp, value: "100%", label: "Satisfaction" },
  { icon: Zap, value: "3+", label: "Years Experience" },
];

const HomePage = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.35]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.04]);
  const heroY = useTransform(scrollYProgress, [0, 0.6], [0, 60]);

  // Preload LCP image (hero poster) — boosts LCP on every page-type
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = exteriorResult;
    link.setAttribute("fetchpriority", "high");
    document.head.appendChild(link);
    return () => { link.remove(); };
  }, []);

  return (
    <PageTransition>
      <div>
        {/* ═══ HERO ═══ */}
        <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden" aria-labelledby="hero-heading">
          <motion.div className="absolute inset-0" style={{ scale: heroScale }}>
            {isMobile === false ? (
              <video
                src={heroVideo.url}
                poster={exteriorResult}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
                aria-hidden="true"
              />
            ) : (
              <img
                src={exteriorResult}
                alt=""
                className="w-full h-full object-cover"
                fetchPriority="high"
                decoding="async"
                aria-hidden="true"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/65 to-background/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/20" />
            {/* Cinematic vignette */}
            <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 55%, hsl(0 0% 3% / 0.35) 100%)" }} />
            {/* Ambient orbs */}
            <div className="orb-glow orb-gold w-[600px] h-[600px] -top-20 -right-20 opacity-25 orb-pulse" />
            <div className="orb-glow orb-gold w-[400px] h-[400px] bottom-20 left-10 opacity-15 animate-sway" />
          </motion.div>

          <motion.div className="relative z-10 container-luxury px-4 md:px-8 py-32" style={{ opacity: heroOpacity, y: heroY }}>
            <div className="max-w-2xl">
              {/* Status badge */}
              <motion.div
                className="inline-flex items-center gap-2.5 glass-ultra rounded-full px-5 py-2.5 mb-8"
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <div className="relative">
                  <div className="w-2 h-2 rounded-full bg-green-400" />
                  <div className="w-2 h-2 rounded-full bg-green-400 absolute inset-0 animate-ping" />
                </div>
                <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-foreground/70">Now Booking — DC, MD, VA</span>
              </motion.div>

              {/* Headline */}
              <h1
                id="hero-heading"
                className="font-display text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.02] mb-6"
              >
                Luxury Mobile{" "}
                <br className="hidden md:block" />
                Detailing That{" "}
                <span className="gold-gradient-text-animated text-glow-strong">Comes To You</span>
              </h1>

              <motion.p
                className="text-muted-foreground text-lg md:text-xl leading-relaxed mb-8 max-w-xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
              >
                Premium interior & exterior detailing. Safe for wraps, PPF, tint & ceramic coatings. Serving Washington DC, Northern Virginia, and Maryland.
              </motion.p>

              {/* Trust badges */}
              <motion.div
                className="flex flex-wrap gap-2.5 mb-10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.85 }}
              >
                {trustBadges.map((badge, i) => (
                  <motion.div
                    key={badge.label}
                    className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground glass-ultra rounded-full px-3.5 py-2"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.9 + i * 0.08 }}
                    whileHover={{ scale: 1.05, borderColor: "hsl(var(--gold) / 0.3)" }}
                  >
                    <badge.icon size={11} className="text-primary" />
                    {badge.label}
                  </motion.div>
                ))}
              </motion.div>

              {/* CTAs — primary dominant, secondary tertiary */}
              <motion.div
                className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.1 }}
              >
                <motion.div whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.98 }}>
                  <Link
                    to="/book?service=Full%20Detail"
                    className="gold-gradient-bg text-primary-foreground px-9 py-4 text-base font-semibold tracking-wide rounded-xl transition-all duration-300 hover:shadow-2xl hover:shadow-primary/30 text-center btn-luxury btn-shine btn-magnetic inline-flex items-center justify-center gap-2 gold-glow"
                    aria-label="Book your premium mobile detail"
                  >
                    Book Now <ArrowRight size={18} />
                  </Link>
                </motion.div>

                {/* Vertical divider on desktop */}
                <span className="hidden sm:block w-px h-8 bg-gradient-to-b from-transparent via-primary/20 to-transparent" aria-hidden />

                <Link
                  to="/plans"
                  className="group inline-flex flex-col sm:items-start items-center text-center sm:text-left -mt-1 sm:mt-0"
                  aria-label="View recurring maintenance plans"
                >
                  <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-0.5">
                    Or subscribe & save
                  </span>
                  <span className="text-sm font-medium text-foreground/80 group-hover:text-primary transition-colors inline-flex items-center gap-1.5">
                    View Maintenance Plans
                    <ArrowRight size={13} className="text-primary opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                  </span>
                </Link>
              </motion.div>
            </div>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
          >
            <motion.div
              className="w-6 h-10 rounded-full border border-primary/20 flex justify-center pt-2"
              animate={{ y: [0, 5, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <motion.div
                className="w-1 h-2 rounded-full bg-primary/50"
                animate={{ opacity: [1, 0.3, 1], y: [0, 4, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </motion.div>
          </motion.div>
        </section>

        {/* ═══ SOCIAL PROOF MARQUEE ═══ */}
        <SocialProofBar />

        {/* ═══ STATS ═══ */}
        <section className="section-padding bg-background relative overflow-hidden">
          <div className="absolute inset-0 bg-grid opacity-20" />
          <div className="absolute inset-0 mesh-gradient" />
          <div className="orb-glow orb-gold w-[500px] h-[500px] -top-40 left-1/4 opacity-12 orb-pulse" />
          <div className="container-luxury relative">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {quickStats.map((stat, i) => (
                <motion.div
                  key={stat.label}
                  className="stat-card hover-lift group relative overflow-hidden"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                >
                  {/* Subtle top accent */}
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <motion.div
                    className="w-14 h-14 rounded-xl gold-gradient-bg flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary/15"
                    whileHover={{ rotate: 10, scale: 1.1 }}
                  >
                    <stat.icon size={22} className="text-primary-foreground" />
                  </motion.div>
                  <AnimatedCounter
                    end={parseFloat(stat.value)}
                    suffix={stat.value.includes("+") ? "+" : stat.value.includes("%") ? "%" : ""}
                    label={stat.label}
                    decimals={stat.value.includes(".") ? 1 : 0}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <div className="glow-line" />

        {/* ═══ BEFORE / AFTER ═══ */}
        <section className="section-padding bg-secondary relative overflow-hidden">
          <div className="absolute inset-0 bg-dots opacity-10" />
          <div className="absolute inset-0 mesh-gradient" />
          <div className="orb-glow orb-gold w-[500px] h-[500px] -top-40 right-1/4 opacity-8 orb-pulse" />
          <div className="container-luxury relative">
            <FadeUp className="text-center mb-14">
              <span className="badge-shimmer mb-4 inline-flex">See The Difference</span>
              <h2 className="font-display text-3xl md:text-5xl font-bold">
                Before <span className="text-muted-foreground/40">&</span>{" "}
                <span className="gold-gradient-text">After</span>
              </h2>
              <p className="text-muted-foreground mt-4 max-w-lg mx-auto text-sm leading-relaxed">
                Drag the slider to see the transformation from our premium mobile detailing service.
              </p>
            </FadeUp>
            <FadeUp>
              <BeforeAfterSlider
                beforeImage={beforeDetail}
                afterImage={afterDetail}
                beforeLabel="Before Detail"
                afterLabel="After Detail"
              />
            </FadeUp>
          </div>
        </section>

        {/* ═══ SERVICES ═══ */}
        <section className="section-padding bg-background relative overflow-hidden">
          <div className="orb-glow orb-gold w-[600px] h-[600px] -top-40 right-0 opacity-10 orb-pulse" />
          <div className="absolute inset-0 bg-dots opacity-15" />
          <div className="absolute inset-0 mesh-gradient" />
          <div className="container-luxury relative">
            <FadeUp className="text-center mb-16">
              <span className="badge-shimmer mb-4 inline-flex">What We Offer</span>
              <h2 className="font-display text-3xl md:text-5xl font-bold">
                The <span className="gold-gradient-text">Full Detail</span>
              </h2>
              <p className="text-muted-foreground mt-4 max-w-lg mx-auto text-sm leading-relaxed">
                Our signature service: a complete interior + exterior transformation. Focused interior-only and exterior-only options also available.
              </p>
            </FadeUp>
            {/* Hero: Full Detail */}
            <div className="mb-12">
              {services.filter((s) => s.featured).map((service) => (
                <motion.div
                  key={service.title}
                  className="group relative overflow-hidden rounded-2xl border-2 border-primary/40 bg-card gold-glow"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  whileHover={{ y: -6 }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                    <div className="relative h-72 md:h-full min-h-[320px] overflow-hidden">
                      <ResponsiveImage
                        source={service.imageResponsive}
                        alt={`${service.title} mobile car detailing service by Metroshine in DC, MD, VA`}
                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                        sizes="(max-width: 768px) 100vw, 50vw"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-card/60 via-transparent to-transparent" />
                      <span className="absolute top-5 left-5 gold-gradient-bg text-primary-foreground text-[11px] font-semibold px-3.5 py-1.5 rounded-lg shadow-xl shadow-primary/30 inline-flex items-center gap-1.5">
                        <Sparkles size={12} /> {service.badge}
                      </span>
                    </div>
                    <div className="p-8 md:p-10 flex flex-col justify-center">
                      <p className="text-primary text-[11px] font-bold mb-3 tracking-[0.2em] uppercase">{service.tiers}</p>
                      <h3 className="font-display text-3xl md:text-4xl font-bold mb-4">
                        <span className="gold-gradient-text">{service.title}</span>
                      </h3>
                      <p className="text-muted-foreground text-base leading-relaxed mb-6">{service.description}</p>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Link
                          to="/book?service=Signature%20Full%20Detail"
                          className="gold-gradient-bg text-primary-foreground px-6 py-3.5 text-sm font-semibold tracking-wider rounded-xl btn-luxury btn-shine inline-flex items-center justify-center gap-2"
                        >
                          Book Full Detail <ArrowRight size={14} />
                        </Link>
                        <Link
                          to="/pricing"
                          className="border border-primary/30 text-foreground px-6 py-3.5 text-sm font-semibold tracking-wider rounded-xl hover:border-primary/60 hover:bg-primary/5 transition-colors inline-flex items-center justify-center"
                        >
                          See Tiers
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Secondary: Focused Services */}
            <div className="text-center mb-6">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                Or choose a focused service
              </p>
            </div>
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {services.filter((s) => !s.featured).map((service) => (
                <ScaleStaggerItem key={service.title}>
                  <motion.div
                    className="group overflow-hidden rounded-2xl border border-border bg-card/60 transition-all duration-500 hover:border-primary/25 h-full flex flex-col md:flex-row"
                    whileHover={{ y: -4 }}
                  >
                    <div className="relative h-44 md:h-auto md:w-2/5 overflow-hidden">
                    <motion.div className="absolute inset-0">
                      <ResponsiveImage
                        source={service.imageResponsive}
                        alt={`${service.title} mobile car detailing service by Metroshine in DC, MD, VA`}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        sizes="(max-width: 768px) 100vw, 40vw"
                      />
                    </motion.div>
                      <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-card via-card/30 to-transparent" />
                    </div>
                    <div className="p-5 md:p-6 flex-1 flex flex-col">
                      <p className="text-muted-foreground text-[10px] font-semibold tracking-[0.2em] uppercase mb-1.5">{service.badge}</p>
                      <h3 className="font-display text-lg font-semibold mb-1 group-hover:text-primary transition-colors">{service.title}</h3>
                      <p className="text-primary text-[11px] font-bold mb-2 tracking-wider">{service.tiers}</p>
                      <p className="text-muted-foreground text-sm leading-relaxed mb-4 flex-1">{service.description}</p>
                      <Link to={`/book?service=${encodeURIComponent(service.title)}`} className="text-primary text-sm font-medium hover:underline inline-flex items-center gap-1.5 group/link">
                        Book {service.title} <ArrowRight size={14} className="transition-transform duration-300 group-hover/link:translate-x-1" />
                      </Link>
                    </div>
                  </motion.div>
                </ScaleStaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>

        <div className="glow-line" />

        {/* ═══ PROCESS TIMELINE ═══ */}
        <ProcessTimeline />

        {/* ═══ WHY METROSHINE ═══ */}
        <ParallaxSection bgImage={exteriorResult} speed={0.15} className="section-padding">
          <div className="container-luxury">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <SlideInLeft>
                <div>
                  <span className="badge-shimmer mb-4 inline-flex">Why Choose Us</span>
                  <h2 className="font-display text-3xl md:text-4xl font-bold mb-8">
                    Why <span className="gold-gradient-text">Metroshine</span>
                  </h2>
                  <div className="space-y-3">
                    {whyUs.map((item, i) => (
                      <motion.div
                        key={i}
                        className="flex items-start gap-4 group p-3.5 rounded-xl transition-all duration-300 hover:bg-card/50"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: 0.15 * i }}
                      >
                        <span className="text-xl mt-0.5 group-hover:scale-125 transition-transform duration-300">{item.icon}</span>
                        <div>
                          <p className="text-foreground font-semibold group-hover:text-primary transition-colors duration-300">{item.text}</p>
                          <p className="text-muted-foreground text-xs mt-0.5">{item.detail}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  <motion.div className="mt-10" whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.98 }}>
                    <Link
                      to="/book"
                      className="inline-flex items-center gap-2 gold-gradient-bg text-primary-foreground px-8 py-3.5 text-sm font-semibold tracking-wider rounded-xl transition-all duration-300 hover:shadow-xl hover:shadow-primary/20 btn-luxury btn-shine btn-magnetic"
                    >
                      Book Now <ArrowRight size={16} />
                    </Link>
                  </motion.div>
                </div>
              </SlideInLeft>
              <SlideInRight>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { src: detailInspection, alt: "Paint inspection during mobile detail in Virginia", extraClass: "" },
                    { src: wheelDetail, alt: "Wheel detailing service near Vienna VA", extraClass: "mt-8" },
                    { src: interiorDetail, alt: "Interior cleaning mobile service DC Maryland", extraClass: "" },
                    { src: exteriorResult, alt: "Exterior detailing result Metroshine DMV", extraClass: "mt-8" },
                  ].map((img, i) => (
                    <motion.div
                      key={i}
                      className={`img-cinematic ${img.extraClass}`}
                      whileHover={{ scale: 1.05, rotate: 1 }}
                      transition={{ duration: 0.4 }}
                    >
                      <img
                        src={img.src}
                        alt={img.alt}
                        className="w-full h-48 object-cover"
                        loading="lazy"
                        width={400}
                        height={300}
                      />
                    </motion.div>
                  ))}
                </div>
              </SlideInRight>
            </div>
          </div>
        </ParallaxSection>

        {/* ═══ TESTIMONIALS ═══ */}
        <TestimonialCarousel />

        {/* ═══ REVIEW HIGHLIGHTS (Google + Yelp) ═══ */}
        <ReviewsHighlight />

        {/* ═══ MAINTENANCE PLANS TEASER ═══ */}
        <section className="section-padding bg-secondary relative overflow-hidden">
          <div className="absolute inset-0 bg-grid opacity-15" />
          <div className="orb-glow orb-gold w-[500px] h-[500px] -top-32 right-0 opacity-10 orb-pulse" />
          <div className="container-luxury relative">
            <FadeUp className="text-center mb-12">
              <span className="badge-shimmer mb-4 inline-flex">Recurring Care</span>
              <h2 className="font-display text-3xl md:text-5xl font-bold">
                Keep It <span className="gold-gradient-text">Showroom Fresh</span>
              </h2>
              <p className="text-muted-foreground mt-4 max-w-xl mx-auto text-sm md:text-base leading-relaxed">
                Subscribe to a maintenance plan and lock in priority scheduling, member pricing, and a vehicle that always looks just-detailed.
              </p>
            </FadeUp>

            <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
              {[
                {
                  name: "Monthly",
                  from: "99",
                  cadence: "/ month",
                  tag: "Reliable monthly refresh",
                  popular: false,
                  features: [
                    "Deep exterior wash",
                    "Full interior vacuum",
                    "Tire shine + trim",
                    "Priority scheduling",
                  ],
                },
                {
                  name: "Bi-Weekly",
                  from: "179",
                  cadence: "/ visit",
                  tag: "Stay polished, smartly priced",
                  popular: true,
                  features: [
                    "Hand wash + spray sealant",
                    "Interior vacuum + dash detail",
                    "Wheels, tires & trim refreshed",
                    "Priority scheduling",
                  ],
                },
                {
                  name: "Weekly",
                  from: "279",
                  cadence: "/ week",
                  tag: "Showroom shine, always",
                  popular: false,
                  features: [
                    "Full hand wash & dry",
                    "Quick interior wipe-down",
                    "Wheels & door jambs",
                    "Guaranteed weekly slot",
                  ],
                },
              ].map((plan) => (
                <ScaleStaggerItem key={plan.name}>
                  <Link
                    to="/plans"
                    className={`group relative h-full rounded-2xl p-7 bg-card/60 border transition-all duration-500 hover:-translate-y-1 flex flex-col ${
                      plan.popular ? "border-primary/60 gold-glow shadow-2xl shadow-primary/10" : "border-border hover:border-primary/30"
                    }`}
                  >
                    {plan.popular && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 gold-gradient-bg text-primary-foreground text-[10px] font-bold tracking-[0.18em] uppercase px-3.5 py-1.5 rounded-full shadow-lg shadow-primary/40 inline-flex items-center gap-1">
                        <Sparkles size={10} /> Most Popular
                      </span>
                    )}

                    {/* Cadence label */}
                    <p className="text-[10px] font-bold tracking-[0.22em] uppercase text-primary mb-3">
                      {plan.name}
                    </p>

                    {/* Strong price hierarchy */}
                    <div className="mb-1">
                      <div className="flex items-baseline gap-1">
                        <span className="text-base font-medium text-muted-foreground">$</span>
                        <span className={`font-display font-bold leading-none gold-gradient-text ${plan.popular ? "text-6xl" : "text-5xl"}`}>
                          {plan.from}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1.5">
                        starting · sedan price <span className="text-foreground/60">{plan.cadence}</span>
                      </p>
                    </div>

                    <p className="text-[13px] text-foreground/80 mt-4 mb-5 italic">{plan.tag}</p>

                    {/* Inclusions list */}
                    <ul className="space-y-2 mb-6 flex-1">
                      {plan.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-[13px] text-muted-foreground">
                          <CheckCircle size={13} className="text-primary mt-0.5 shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA row */}
                    <span className={`inline-flex items-center justify-center gap-1.5 text-sm font-semibold rounded-xl py-2.5 transition-all ${
                      plan.popular
                        ? "gold-gradient-bg text-primary-foreground shadow-lg shadow-primary/20 group-hover:shadow-primary/40"
                        : "border border-primary/30 text-primary group-hover:bg-primary/5 group-hover:border-primary/60"
                    }`}>
                      {plan.popular ? "Start Bi-Weekly" : "View plan"}
                      <ArrowRight size={14} />
                    </span>
                  </Link>
                </ScaleStaggerItem>
              ))}
            </StaggerContainer>

            <FadeUp className="text-center mt-10">
              <Link
                to="/plans"
                className="inline-flex items-center gap-2 gold-gradient-bg text-primary-foreground px-8 py-3.5 text-sm font-semibold tracking-wider rounded-xl btn-luxury btn-shine"
              >
                Compare All Plans <ArrowRight size={14} />
              </Link>
              <p className="text-xs text-muted-foreground mt-3">Cancel or switch any time · Member pricing on add-ons</p>
            </FadeUp>
          </div>
        </section>

        {/* ═══ CTA ═══ */}
        <CTABanner />

        {/* ═══ FLEET BANNER ═══ */}
        <section className="section-padding-sm bg-background relative overflow-hidden">
          <div className="container-luxury">
            <FadeUp>
              <Link
                to="/dealership-fleet"
                className="group block luxury-card-premium gold-border rounded-2xl p-6 md:p-10 relative overflow-hidden hover:gold-glow transition-all duration-500"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="relative flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
                  <div className="flex flex-col md:flex-row items-center gap-5">
                    <div className="w-16 h-16 rounded-2xl gold-gradient-bg flex items-center justify-center shrink-0">
                      <Truck size={28} className="text-primary-foreground" />
                    </div>
                    <div>
                      <span className="inline-block text-[10px] font-semibold tracking-widest uppercase text-primary mb-1">
                        Commercial Services
                      </span>
                      <h3 className="font-display text-xl md:text-2xl font-bold mb-1">
                        Fleet & Dealership Detailing
                      </h3>
                      <p className="text-sm text-muted-foreground max-w-md">
                        Volume pricing for dealerships, rental companies, and multi-vehicle operations. From $25/car.
                      </p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-2 gold-gradient-bg text-primary-foreground px-6 py-3 text-sm font-semibold rounded-xl btn-luxury whitespace-nowrap">
                    Fleet Services <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            </FadeUp>
          </div>
        </section>

        {/* ═══ SERVICE AREA ═══ */}
        <ServiceAreaMap />

        {/* ═══ FAQ ═══ */}
        <FAQSection />

        {/* ═══ INSTAGRAM ═══ */}
        <InstagramFeed />
      </div>
    </PageTransition>
  );
};

export default HomePage;
